import { useEffect, useState } from "react";
import api from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout } from "./ClientHome";
import { Pill, ExternalLink, Tag, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Supplements() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    api.get("/supplements").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/client/supplement-recs").then((r) => setRecs(r.data)).catch(() => {});
  }, []);

  return (
    <ClientLayout>
      <div className="max-w-2xl mx-auto w-full space-y-5" data-testid="supplements-page">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <Pill className="w-7 h-7 text-primary" /> {t("supp.title")}
        </h1>
        <p className="text-muted-foreground text-sm">{t("supp.sub")}</p>
        <div className="kc-card p-4 border-primary/40 flex items-center gap-3" data-testid="pushmore-discount-card">
          <Tag className="w-5 h-5 text-primary shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block font-bold text-sm">{t("supp.discount")}</span>
            <span className="block text-xs text-muted-foreground">{t("supp.discountSub")}</span>
          </span>
          <button data-testid="pushmore-copy-button"
            onClick={async () => { try { await navigator.clipboard.writeText("ADSTUDIO27"); toast.success(t("ref.copied")); } catch {} }}
            className="kc-btn-primary !px-4 !py-2 text-xs shrink-0 inline-flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> ADSTUDIO27
          </button>
        </div>
        {recs.length > 0 && (
          <section className="space-y-3" data-testid="recommended-supplements">
            <p className="kc-eyebrow !text-[#E0597F]">{t("supp.forYou")}</p>
            {recs.map((s) => (
              <div key={s.rec_id} className="kc-card p-5 border-primary/50 kc-animate" data-testid={`rec-card-${s._id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-lg leading-tight">{s.name}</p>
                    {s.description && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{s.description}</p>}
                  </div>
                  {s.price != null && <span className="font-mono font-black text-xl text-primary shrink-0">€{Number(s.price).toFixed(2)}</span>}
                </div>
                {s.note && (
                  <p className="text-xs bg-primary/10 border border-primary/30 rounded-xl px-3 py-2 mt-3 italic" data-testid={`rec-note-${s._id}`}>
                    "{s.note}"
                  </p>
                )}
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`rec-buy-${s._id}`}
                    className="kc-btn-primary inline-flex items-center gap-2 !px-5 !py-2 text-sm mt-4">
                    {t("supp.buy")} <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </section>
        )}
        {items === null ? (
          <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <div className="kc-card p-8 text-center" data-testid="supplements-empty">
            <Pill className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">{t("supp.empty")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="kc-eyebrow !text-[#E0597F]">{t("supp.library")}</p>
            {items.map((s, i) => (
              <div key={s._id} className="kc-card p-5 kc-animate" style={{ animationDelay: `${0.06 * i}s` }} data-testid="supplement-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-lg leading-tight" data-testid="supplement-name">{s.name}</p>
                    {s.description && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{s.description}</p>}
                  </div>
                  {s.price != null && (
                    <span className="font-mono font-black text-xl text-primary shrink-0" data-testid="supplement-price">
                      €{Number(s.price).toFixed(2)}
                    </span>
                  )}
                </div>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid="supplement-buy-link"
                    className="kc-btn-primary inline-flex items-center gap-2 !px-5 !py-2 text-sm mt-4">
                    {t("supp.buy")} <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
